import xs from "xstream";
import * as actions from "../actions";
import * as ActionTypes from "../actions/types";
import { BASE_URL, NEW_BASE_URL } from "../config";
import sampleCombine from "xstream/extra/sampleCombine";
import {
  createJsonApiFilter,
  jsonApiIncludeRelationships,
} from "../constants/jsonapi_helpers";
import { generalHeader } from "./headers";
import { apiProduct } from "../reducers/apiProduct";
import { createBackendRequest } from "../mappers/backendMappers";

export function fetchApiProduct(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_API_PRODUCT
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => {
      // Create request body for new backend API
      console.log("Fetching product with ID:", action.payload.apiProductId);
      const requestBody = createBackendRequest(
        {
          Id: action.payload.apiProductId,
        },
        userId
      );

      return {
        url: NEW_BASE_URL + "/Product/get",
        category: "fetchApiProduct",
        method: "POST",
        headers: {
          ...generalHeader(csrfToken, true),
          "Content-Type": "application/json",
        },
        send: requestBody,
        // withCredentials: true, // Commented out due to CORS issues
      };
    });

  let httpResponse$ = sources.HTTP.select("fetchApiProduct")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      console.log("Backend response:", response.body);
      // Get the product data directly from backend
      const payload = response.body?.Payload || response.body?.payload;
      if (response.body && payload) {
        // Pass backend data directly to reducer without transformation
        console.log("Product data from backend:", payload);
        return actions.fetchApiProductSuccess(payload);
      }
      // Return null if no data
      return actions.fetchApiProductSuccess(null);
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function fetchComparisonTable(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);

  let request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_API_PRODUCT_SUCCESS
  )
    .compose(sampleCombine(csrfToken$))
    .map(([action, csrfToken]) => {
      // Handle backend DTO format - check for comparisonTable field
      const productData = action.payload;
      const comparisonTableId =
        productData?.comparisonTable || productData?.ComparisonTable;

      if (comparisonTableId) {
        return {
          url:
            BASE_URL +
            `/jsonapi/node/comparison_table/${comparisonTableId}` +
            `?` +
            jsonApiIncludeRelationships([
              "field_comparison_row",
              "field_product",
            ]) +
            `&fields[comparison_boolean_row]=field_boolean_row_title,field_boolean_row_values` +
            `&fields[comparison_category]=field_category_title` +
            `&fields[comparison_text_row]=field_text_row_title,field_text_row_values` +
            `&fields[title_text]=field_body,field_title`,
          category: "fetchComparisonTable",
          method: "GET",
          headers: generalHeader(csrfToken, true),
          withCredentials: true,
        };
      } else {
        return;
      }
    });

  let httpResponse$ = sources.HTTP.select("fetchComparisonTable")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => actions.fetchComparisonTableSuccess(response.body));

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function fetchComparisonTableFailed(sources) {
  const response$ = sources.HTTP.select("fetchComparisonTable")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status !== 200);

  const action$ = response$.map((response) =>
    actions.fetchComparisonTableFailed(response)
  );

  return {
    ACTION: action$,
  };
}

export function fetchApiProductFailed(sources) {
  const response$ = sources.HTTP.select("fetchApiProduct")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status !== 200);

  const action$ = response$.map((response) =>
    actions.fetchApiProductFailed(response)
  );

  return {
    ACTION: action$,
  };
}

export function subscribeApiProductToApplication(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  let request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => {
      // Create request body for new backend API
      const requestBody = createBackendRequest(
        {
          ApplicationId: action.payload.planUuid,
          ProductId: action.payload.apiProductUuid,
        },
        userId
      );

      return {
        url: NEW_BASE_URL + "/AppSubscription/create",
        category: "subscribeApiProductToApplication",
        method: "POST",
        headers: generalHeader(csrfToken),
        send: requestBody,
        withCredentials: true,
      };
    });

  let httpResponse$ = sources.HTTP.select("subscribeApiProductToApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) =>
      actions.subscribeApiProductToApplicationSuccess(response.body)
    );
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function subscribeApiProductToApplicationFailed(sources) {
  const response$ = sources.HTTP.select("subscribeApiProductToApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.subscribeApiProductToApplicationFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    return actions.subscribeApiProductToApplicationFailed(response);
  });

  return {
    ACTION: action$,
  };
}

// fetchChangeLog/fetchChangeLogFailed removed 2026-09-20: changeLog is embedded on the
// product document itself (see ExternalApiProductDataSource.cs / ProductDto.ChangeLog in core),
// not a separate entity - this cycle's /ChangeLog/list call always returned empty against a
// dead backing table and fired on every product page view. See devportal-manager-cms-feature memory.
